import axios from "@/plugins/axios";
import qs from "qs";
import utils from "@/stores/utils";
import store from "@/stores/store";
import $utils from '@/utils';
import { executeBulkAction as executeBulk } from "@/utils/bulk";
import { withSelectedIds } from "@/utils/selectionStats";
import { CSV_EXPORT_LIMIT } from '@/constants/csv-export';

const {
    getStorageKey,
    loadFiltersFromStorage,
    saveFiltersToStorage,
    mergeUserAndSectionFilters,
    extractUserFiltersFromEffective,
    getSectionConstraintKeys,
    omitFilterKeys,
} = utils;

/** Shared across all provider-order tabs (section constraints applied at query time). */
const PROVIDERORDERS_FILTERS_STORAGE_KEY = getStorageKey('providerorders');
const DEFAULT_USER_FILTERS = {
    limit: 20,
    ordering: "-order_id",
    include_archived: false,
};

const ALL_TAB_DEFAULT_STATUSES = [
    "draft",
    "to_be_processed",
    "waiting",
    "processing",
    "completed",
];

const hasStatusFilter = (status) => {
    if (status == null || status === "") {
        return false;
    }
    if (Array.isArray(status)) {
        return status.length > 0;
    }
    return true;
};

const isAllTabDefaultStatus = (status) => {
    if (!Array.isArray(status) || status.length !== ALL_TAB_DEFAULT_STATUSES.length) {
        return false;
    }
    const selected = new Set(status);
    return ALL_TAB_DEFAULT_STATUSES.every((item) => selected.has(item));
};

const normalizeProviderOrdersFilters = (filters, section) => {
    const next = { ...(filters || {}) };
    if (section === "all" && isAllTabDefaultStatus(next.status)) {
        delete next.status;
    }
    return next;
};

const state = {
    section: null,
    providerorders: [],
    /** User-chosen filters persisted across tabs (no section constraints). */
    providerorders_user_filters: { ...DEFAULT_USER_FILTERS },
    /** Effective filters for API (user + section overlay). */
    providerorders_filters: { limit: 20, offset: 0, ordering: "-order_id", include_archived: false },
    providerorders_user_filters_hydrated: false,
    providerorders_count: 0,
    providerorders_loading: false,
    providerorders_loading_errors: null,
    providerorders_cancel_source: null,
    orders_stats: {},
    orders_stats_loaded: false,
    orders_stats_loading: false,
    orders_stats_loading_errors: null,
    orders_stats_cancel_source: null,
    orders_stats_filters_key: null,
    selected_items: [],
    bulk_action_loading: false,
};

/**
 * Hard tab constraints only — do not put soft defaults (e.g. include_archived: false)
 * here or they would always win over user prefs.
 */
let getSectionFilters = function (section, customer) {
    const filters = {};
    if (customer) {
        filters.customer = customer;
    }
    switch (section) {
        case "all":
            // Keep canceled orders out of the default "all" query in
            // buildProviderOrdersRequestFilters, not here: pinning `status`
            // made the UI filter always look active and reset a no-op.
            break;
        case "drafts":
            filters.status = "draft";
            filters.validated = false;
            break;
        case "tovalidate":
            filters.needs_buyer_validation = true;
            break;
        case "pending_approval":
            filters.purchase_approval_status = "pending";
            break;
        case "rejected_approval":
            filters.purchase_approval_status = "rejected";
            break;
        case "toapprove":
            filters.pending_purchase_approval_for_me = true;
            break;
        case "tohandle":
            filters.status = "to_be_processed";
            break;
        case "waiting":
            filters.status = "waiting";
            break;
        case "processing":
            filters.status = "processing";
            break;
        case "missinginvoice":
            filters.status = "completed";
            filters.missinginvoice = true;
            break;
        case "archived":
            filters.archived = true;
            filters.customer_archived = true;
            break;
    }
    if (store.getters["session/current_entity_profile"] == "customer") {
        filters.suborder = false;
    }
    return filters;
};

function loadSharedProviderOrdersUserFilters(section) {
    const shared = loadFiltersFromStorage(PROVIDERORDERS_FILTERS_STORAGE_KEY, null);
    if (shared) {
        return extractUserFiltersFromEffective(
            shared,
            getSectionFilters(section || 'drafts'),
            {},
        );
    }
    const legacy = loadFiltersFromStorage(
        getStorageKey('providerorders', section || 'drafts'),
        { ...DEFAULT_USER_FILTERS },
    );
    return extractUserFiltersFromEffective(
        legacy,
        getSectionFilters(section || 'drafts'),
        {},
    );
}

function persistProviderOrdersUserFilters(userFilters) {
    saveFiltersToStorage(PROVIDERORDERS_FILTERS_STORAGE_KEY, userFilters);
}

function rebuildProviderOrdersFilters(state, { resetOffset = false } = {}) {
    const section = state.section || 'drafts';
    const previousOffset = state.providerorders_filters?.offset ?? 0;
    state.providerorders_filters = normalizeProviderOrdersFilters(
        mergeUserAndSectionFilters(
            state.providerorders_user_filters,
            getSectionFilters(section, state.provider),
        ),
        section,
    );
    state.providerorders_filters.offset = resetOffset ? 0 : previousOffset;
}

const buildProviderOrdersRequestFilters = (state) => {
    const filters = { ...state.providerorders_filters };
    if (state.section === "all" && !hasStatusFilter(filters.status)) {
        filters.status = ALL_TAB_DEFAULT_STATUSES;
    }
    return filters;
};

const mutations = {
    updateOrdersStats(state, orders_stats) {
        state.orders_stats = orders_stats;
        state.orders_stats_loaded = true;
    },
    updateOrdersStatsLoading(state, loading) {
        state.orders_stats_loading = loading;
    },
    updateOrdersStatsLoadingErrors(state, errors) {
        state.orders_stats_loading_errors = errors;
    },
    updateOrdersStatsCancelSource(state, cancel_source) {
        state.orders_stats_cancel_source = cancel_source;
    },
    updateOrdersStatsFiltersKey(state, filters_key) {
        state.orders_stats_filters_key = filters_key;
    },
    updateSection(state, mode) {
        state.providerorders = [];
        state.providerorders_count = 0;
        state.section = mode;
        if (!state.providerorders_user_filters_hydrated) {
            state.providerorders_user_filters = {
                ...DEFAULT_USER_FILTERS,
                ...loadSharedProviderOrdersUserFilters(mode),
            };
            state.providerorders_user_filters_hydrated = true;
            persistProviderOrdersUserFilters(state.providerorders_user_filters);
        }
        rebuildProviderOrdersFilters(state, { resetOffset: true });
    },

    updateProviderOrders(state, providerorders) {
        state.providerorders = providerorders;
    },
    updateProviderOrdersCount(state, count) {
        state.providerorders_count = count;
    },
    updateProviderOrdersLoading(state, loading) {
        state.providerorders_loading = loading;
    },
    updateProviderOrdersLoadingErrors(state, errors) {
        state.providerorders_loading_errors = errors;
    },
    updateProviderOrdersFilters(state, providerorders_filters) {
        const section = state.section || 'drafts';
        state.providerorders_user_filters = extractUserFiltersFromEffective(
            providerorders_filters,
            getSectionFilters(section, state.provider),
            state.providerorders_user_filters,
        );
        state.providerorders_user_filters_hydrated = true;
        persistProviderOrdersUserFilters(state.providerorders_user_filters);
        state.providerorders_filters = normalizeProviderOrdersFilters(
            mergeUserAndSectionFilters(
                state.providerorders_user_filters,
                getSectionFilters(section, state.provider),
            ),
            section,
        );
        state.providerorders_filters.offset = providerorders_filters?.offset ?? 0;
        if (state.providerorders_filters.limit == null) {
            state.providerorders_filters.limit =
                state.providerorders_user_filters.limit || 20;
        }
    },

    clearProviderOrdersOrdering(state) {
        const { ordering, ...userFilters } = state.providerorders_user_filters;
        state.providerorders_user_filters = userFilters;
        persistProviderOrdersUserFilters(state.providerorders_user_filters);
        const { ordering: _ignored, ...filters } = state.providerorders_filters;
        state.providerorders_filters = filters;
    },

    updateProviderOrdersCancelSource(state, source) {
        state.providerorders_cancel_source = source;
    },
    updateSelectedItems(state, selected_items) {
        state.selected_items = selected_items;
    },
    updateBulkActionLoading(state, loading) {
        state.bulk_action_loading = loading;
    },
};

const actions = {
    changeSection({ commit, dispatch, state }, mode) {
        if (mode == state.section) {
            return;
        }
        commit("updateSection", mode);
        commit("updateOrdersStatsFiltersKey", null);
        dispatch("fetchProviderOrders");
        dispatch("session/fetchStats", null, { root: true });
    },

    exportProviderOrdersAsCSV({ commit, dispatch, state }, params) {
        let filters = {};
        if (params.filtered) {
            filters = Object.assign({}, buildProviderOrdersRequestFilters(state));
            filters.offset = null;
        }
        filters.format = "csv";
        filters.limit = CSV_EXPORT_LIMIT;
        
        $utils.downloadFile({
            url: "/api/providerorders/",
            params: filters,
        }, $utils.getFilename('providerOrders'));
    },

    exportProviderOrderItemsAsCSV({ commit, dispatch, state }, params) {
        let filters = {};
        if (params.filtered) {
            const skip = new Set(["limit", "offset", "ordering"]);
            const requestFilters = buildProviderOrdersRequestFilters(state);
            for (let key in requestFilters) {
                if (skip.has(key)) {
                    continue;
                }
                const value = requestFilters[key];
                if (value === null || value === undefined || value === "") {
                    continue;
                }
                filters["order_" + key] = value;
            }
        }
        filters.format = "csv";
        filters.limit = CSV_EXPORT_LIMIT;
        delete filters.offset;
        delete filters.order_offset;
        
        $utils.downloadFile({
            url: "/api/providerorderitems/",
            params: filters,
        }, $utils.getFilename('providerOrderItems'));
    },

    async fetchProviderOrders({ commit, dispatch, state }, params) {
        commit("updateProviderOrdersLoading", true);
        commit("updateProviderOrdersLoadingErrors", null);

        if (state.providerorders_cancel_source) {
            state.providerorders_cancel_source.cancel("canceled");
        }

        const cancelSource = axios.CancelToken.source();
        commit("updateProviderOrdersCancelSource", cancelSource);

        try {
            const response = await axios.get("/api/providerorders/", {
                params: buildProviderOrdersRequestFilters(state),
                cancelToken: state.providerorders_cancel_source?.token,
            });
            commit("updateProviderOrdersLoading", false);
            commit("updateProviderOrders", response.data.results);
            commit("updateProviderOrdersCount", response.data.count);
            commit("updateProviderOrdersCancelSource", null);

            const { limit, offset, ...filtersWithoutPagination } =
                buildProviderOrdersRequestFilters(state);
            const statsFilterKey = JSON.stringify(filtersWithoutPagination);
            if (statsFilterKey !== state.orders_stats_filters_key) {
                commit("updateOrdersStatsFiltersKey", statsFilterKey);
                dispatch("fetchOrdersStats");
            }
            return response;
        } catch (xhr_error) {
            if (axios.isCancel(xhr_error)) {
                return;
            }
            // An ordering value rejected by the API is persisted in localStorage and would
            // keep breaking every subsequent request, so drop it and retry once.
            if (
                !params?.retriedWithoutOrdering &&
                xhr_error.response?.status === 400 &&
                xhr_error.response?.data?.ordering &&
                state.providerorders_filters.ordering
            ) {
                commit("updateProviderOrdersCancelSource", null);
                commit("clearProviderOrdersOrdering");
                return dispatch("fetchProviderOrders", {
                    ...(params || {}),
                    retriedWithoutOrdering: true,
                });
            }
            const error = utils.handleError(xhr_error);
            commit("updateProviderOrdersLoadingErrors", error.details);
            commit("updateProviderOrdersLoading", false);
            commit("updateProviderOrdersCancelSource", null);
            return Promise.reject(error);
        }
    },

    async assignProviderOrder({ commit, dispatch, state }, params) {
        const url = `/api/providerorders/${params.order.id}/assign/`;

        try {
            const response = await axios.put(url, { user: params.user });
            dispatch("fetchProviderOrders");
            dispatch("session/fetchStats", null, { root: true });
            return response;
        } catch (xhr_error) {
            return Promise.reject(utils.handleError(xhr_error));
        }
    },

    async fetchOrdersStats({ commit, dispatch, state }, params) {
        const permissions = store.getters["session/current_user_permissions"] || [];
        const canViewStats =
            permissions.indexOf("order.view_order") !== -1
            || permissions.indexOf("order.view_providerorder") !== -1
            || $utils.userHasScope(
                "read_providerorders",
                "write_providerorders",
                "validate_providerorders",
            );
        if (!canViewStats) {
            return;
        }

        commit("updateOrdersStatsLoading", true);
        commit("updateOrdersStatsLoadingErrors", null);

        if (state.orders_stats_cancel_source) {
            state.orders_stats_cancel_source.cancel("canceled");
        }

        const cancelSource = axios.CancelToken.source();
        commit("updateOrdersStatsCancelSource", cancelSource);

        try {
            const response = await axios.get("/api/providerorders/stats/", {
                params: withSelectedIds(
                    buildProviderOrdersRequestFilters(state),
                    state.selected_items,
                ),
                cancelToken: state.orders_stats_cancel_source?.token
            });
            commit("updateOrdersStatsLoading", false);
            commit("updateOrdersStats", response.data);
            commit("updateOrdersStatsCancelSource", null);
            return response;
        } catch (xhr_error) {
            if (axios.isCancel(xhr_error)) {
                return;
            }
            const error = utils.handleError(xhr_error);
            commit("updateOrdersStatsLoadingErrors", error.details);
            commit("updateOrdersStatsLoading", false);
            throw error;
        }
    },

    async duplicateProviderOrder({ commit, dispatch, state }, params) {
        try {
            const url = `/api/providerorders/${params.providerorder.id}/duplicate/`;
            const response = await axios.post(url);
            dispatch("fetchProviderOrders");
            dispatch("session/fetchStats", null, { root: true });
            return response;
        } catch (xhr_error) {
            return Promise.reject(utils.handleError(xhr_error));
        }
    },

    async bulkAction({ commit, dispatch, state }, params) {
        commit("updateBulkActionLoading", true);
        try {
            const result = await executeBulk({
                bulkActionUrl: "/api/providerorders/bulk/",
                action: params.action,
                instances: state.selected_items.map(item => item.id),
                payload: params.payload || {},
                context: params.context,
                getIdFn: params.getIdFn,
                successMessage: params.successMessage,
            });
            const items = state.selected_items;
            commit("updateSelectedItems", []);
            dispatch("fetchProviderOrders");
            dispatch("session/fetchStats", null, { root: true });
            return { ...result, items };
        } catch (xhr_error) {
            throw utils.handleError(xhr_error);
        } finally {
            commit("updateBulkActionLoading", false);
        }
    },

    async init({ commit, dispatch, state }, params) {
        // Initialize the store with section from route params or default
        const section = params?.section || state.section || 'drafts';
        if (section !== state.section) {
            commit('updateSection', section);
        }
        // Fetch orders and global stats
        dispatch('fetchProviderOrders');
        dispatch('session/fetchStats', null, { root: true });
    },
};

const getters = {
    /**
     * Filters bound to Table UI: section constraints are hidden so tabs don't
     * surface forced values as user chips. API still uses providerorders_filters.
     */
    providerorders_table_filters(state) {
        const section = state.section || 'drafts';
        return omitFilterKeys(
            state.providerorders_filters,
            getSectionConstraintKeys(getSectionFilters(section, state.provider)),
        );
    },
};

export default {
    namespaced: true,
    state,
    getters,
    actions,
    mutations,
};
